import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, X, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fromISO = (s) => new Date(s + "T00:00:00");

export default function AvailabilityManager() {
  const [dates, setDates] = useState([]); // Date[]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noProfile, setNoProfile] = useState(false);

  useEffect(() => {
    api
      .get("/artists/me")
      .then((r) => setDates((r.data.availability || []).map(fromISO)))
      .catch((e) => {
        if (e.response?.status === 404) setNoProfile(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const iso = dates.map(toISO).sort();
      await api.put("/artists/me/availability", { availability: iso });
      toast.success("Disponibilités enregistrées");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = (iso) => setDates((d) => d.filter((x) => toISO(x) !== iso));

  if (loading) return <p className="text-[#A19E95] text-center py-20">Chargement...</p>;

  if (noProfile)
    return (
      <div className="text-center py-20 border border-white/10 rounded-2xl" data-testid="availability-noprofile">
        <CalendarDays className="text-[#C9A24B] mx-auto mb-4" size={40} />
        <p className="text-white font-serif text-2xl mb-2">Aucun profil artiste lié</p>
        <p className="text-[#A19E95]">Contactez l'administrateur MYSE pour lier votre compte à votre profil artiste.</p>
      </div>
    );

  const sorted = [...dates].sort((a, b) => a - b);

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="availability-manager">
      <div className="rounded-2xl border border-white/10 bg-[#121216] p-4 flex justify-center">
        <Calendar
          mode="multiple"
          selected={dates}
          onSelect={(d) => setDates(d || [])}
          disabled={{ before: new Date() }}
          data-testid="availability-calendar"
          className="text-white"
        />
      </div>

      <div>
        <h3 className="font-serif text-2xl text-white mb-1">Vos créneaux disponibles</h3>
        <p className="text-[#A19E95] text-sm mb-5">
          Sélectionnez les dates auxquelles vous êtes disponible. Elles apparaîtront sur votre page artiste.
        </p>

        {sorted.length === 0 ? (
          <p className="text-[#6E6B63] text-sm mb-5">Aucune date sélectionnée pour l'instant.</p>
        ) : (
          <div className="flex flex-wrap gap-2 mb-6">
            {sorted.map((d) => {
              const iso = toISO(d);
              return (
                <span
                  key={iso}
                  data-testid={`availability-chip-${iso}`}
                  className="flex items-center gap-2 text-xs text-[#C9A24B] border border-[#C9A24B]/30 pl-3 pr-2 py-1.5 rounded-full"
                >
                  {d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  <button onClick={() => remove(iso)} className="hover:text-white" aria-label="Retirer">
                    <X size={13} />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-availability-btn"
          className="gold-bg text-black font-semibold rounded-full px-8"
        >
          <Save size={16} className="mr-2" /> {saving ? "Enregistrement..." : "Enregistrer mes disponibilités"}
        </Button>
      </div>
    </div>
  );
}
